/**
 * Pure helpers over the WOO's availableMaterials custom field, written by the
 * dynamics365-connector's available-materials sync. The field is a map keyed
 * by item number:
 *
 *   {
 *     "<itemNumber>": {
 *       requiredQty: 24,
 *       qtyPerUnit: 0.024,   // quantity consumed per produced part
 *       unit: "pcs",
 *       lps: [ { lp, onHand, warehouse, location?, batch? }, ... ]
 *     }
 *   }
 *
 * The connector does not write a description per material; itemNumber is the
 * display name throughout the picking UI.
 */

/**
 * Flattens the availableMaterials map into an array of requirement rows:
 * { itemNumber, requiredQty, qtyPerUnit, unit, lps }.
 * Empty array when the WOO has no synced materials.
 */
export const requirementsFromMaterials = (materials) => {
  return Object.keys(materials || {}).map((itemNumber) => {
    const material = materials[itemNumber];
    return {
      itemNumber,
      ...material,
      lps: material.lps || [],
    };
  });
};

/**
 * Resolve a scanned LP against requirement rows (as produced by
 * requirementsFromMaterials). Returns { itemNumber, lpInfo } if found, else null.
 */
export const findLpInRequirements = (requirements, lp) => {
  const normalized = String(lp || '')
    .trim()
    .toUpperCase();
  if (!normalized) {
    return null;
  }
  const match = (requirements || []).reduce((found, req) => {
    if (found) {
      return found;
    }
    const lpInfo = (req.lps || []).find((e) => {
      return String(e.lp).toUpperCase() === normalized;
    });
    return lpInfo ? { itemNumber: req.itemNumber, lpInfo } : null;
  }, null);
  return match;
};

/**
 * Material quantity for display: locale-grouped, with up to `decimals` decimals.
 * The default of 4 matches the precision picks are recorded with (clampPickQty).
 */
export const formatMaterialQty = (qty, decimals = 4) => {
  return (Number(qty) || 0).toLocaleString(undefined, {
    maximumFractionDigits: decimals,
  });
};

/**
 * qtyPerUnit as a material : parts ratio. 2 → "2:1" (2 of material per part),
 * 0.25 → "1:4" (1 of material per 4 parts), 1.5 → "1.5:1". Null when unset.
 */
export const formatMaterialRatio = (qtyPerUnit) => {
  if (!qtyPerUnit) return null;
  if (qtyPerUnit >= 1) return `${formatMaterialQty(qtyPerUnit, 2)}:1`;
  return `1:${formatMaterialQty(1 / qtyPerUnit, 2)}`;
};

// Picked quantities keep up to 4 decimals: materials are weighed or measured
// (lb, gal), and the rounding drops float noise from the ±1 steppers.
const QTY_PRECISION = 10000;

// A typed quantity: digits with an optional decimal part ("12", "0.5", ".5", "3.").
const TYPED_QTY = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

const parsePickQty = (raw) => {
  if (typeof raw === 'number') return raw;
  const typed = String(raw ?? '')
    .trim()
    .replace(',', '.');
  return TYPED_QTY.test(typed) ? Number(typed) : Number.NaN;
};

/**
 * A picked quantity clamped to 0..maxQty. `raw` may be typed text ("0.5" or
 * "0,5"); anything that is not a whole number or decimal, such as "12abc", is 0.
 */
export const clampPickQty = (raw, maxQty) => {
  const n = parsePickQty(raw);
  if (!Number.isFinite(n)) return 0;
  const clamped = Math.min(Math.max(0, n), maxQty);
  return Math.round(clamped * QTY_PRECISION) / QTY_PRECISION;
};
